// Pricing migration 17 (ADO-2248): the one price component every product surface
// renders (ADO-2248 DEC-2). It owns the price's states, so no surface tells them apart.
//
// - Flag off (ADO-2248 D1): the stand-in unit price from `provisionalPrice.ts`, as today.
// - Flag on, priced: the endpoint's unit price through `formatMoney`; the web never computes it.
// - Flag on, not priced: the designer's price states (ADO-2248 PS-1 to PS-9, the "Price
//   states" board), in the designer's words:
//   - loading (or not asked yet): "€––.––" in the host's own type, so nothing moves when the
//     price lands; after about 3 seconds, "Loading price…". A screen reader hears "Loading
//     price" throughout, never the placeholder (PS-7);
//   - failed: "Price unavailable" and a Retry button naming the product, which asks again for
//     this SKU only (PS-4);
//   - unpriced (any reason, a 403 included, PS-5): "Price not available" and "Ask your BWG rep".
//   Never €0.00 and never the stand-in (PS-6). No state uses a live region (PS-7).
// - Flag on, no entry chosen: nothing is asked (PS-9), so the slot stays EMPTY and keeps its
//   place, as before the states existed (ADO-2248 D8).
//
// It renders into the caller's own element and class, so each surface keeps its layout and
// the price's type; the state lines carry their own small type.

import { useEffect, useMemo, useState } from 'react';
import { useSessionStore } from '../auth/useSessionStore';
import { useSessionMarginShown } from '../basket/sessionCart';
import { formatMoney, type MoneyValue } from '../shared/money';
import { shownWasRrp } from './priceExplanation';
import { provisionalUnitPrice } from './provisionalPrice';
import { realPricesEnabled } from './realPrices';
import {
  useCurrentPriceKey,
  usePriceAccountCode,
  usePriceEntry,
  usePriceStore,
  type PriceEntry,
} from './usePriceStore';
import styles from './ProductPrice.module.css';

/** What the slot shows: the stand-in (flag off), nothing asked (no entry chosen), or one of
 *  the store's states. */
type PriceSlotState = 'stand-in' | 'not-asked' | 'loading' | PriceEntry['state'];

/** How long the placeholder shows before "Loading price…" replaces it (the designer's "about
 *  3 seconds"). */
export const LOADING_TEXT_DELAY_MS = 3000;

/** The designer's copy, word for word (the "Price states" board). The placeholder is "€" then
 *  two en-dashes, a full stop and two en-dashes. */
const PLACEHOLDER = '€\u2013\u2013.\u2013\u2013';
const LOADING_TEXT = 'Loading price\u2026';
const LOADING_SPOKEN = 'Loading price';
const FAILED_TEXT = 'Price unavailable';
const UNPRICED_TEXT = 'Price not available';
const UNPRICED_HINT = 'Ask your BWG rep';

export interface ProductPriceProps {
  readonly sku: string;
  /** The product's name, for Retry's accessible name (PS-7). */
  readonly productName: string;
  /** The host element, so the price keeps the surface's markup (`p` in a card footer). */
  readonly as?: 'p' | 'span' | undefined;
  /** The host's own price class. */
  readonly className?: string | undefined;
}

export interface ProductPriceFigureProps extends ProductPriceProps {
  /** The SKU's price state, from a lookup other than the shared store (the typeahead's
   *  dropdown-scoped one, DEC-3). `undefined` means not answered yet. */
  readonly entry: PriceEntry | undefined;
  /** Whether a price is asked for at all: `false` with no entry chosen (PS-9). */
  readonly asked: boolean;
  /** The failed state's Retry: ask again for this SKU only (PS-4). */
  readonly onRetry: () => void;
  /** Whether "Loading price…" has replaced the placeholder, when the caller times it itself
   *  (the typeahead row, whose layout follows it); otherwise the figure times it. */
  readonly loadingTooLong?: boolean | undefined;
}

/** A figure that may be shown: priced, above zero. */
function shownFigure(entry: PriceEntry | undefined): string | null {
  if (entry?.state !== 'priced' || !(entry.unitPrice.amount > 0)) return null;
  return formatMoney(entry.unitPrice);
}

/** `true` once `loading` has lasted {@link LOADING_TEXT_DELAY_MS}; back to `false` whenever it
 *  stops, so a retry starts the wait again. */
export function useLoadingTooLong(loading: boolean): boolean {
  const [tooLong, setTooLong] = useState(false);
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => setTooLong(true), LOADING_TEXT_DELAY_MS);
    return () => {
      clearTimeout(timer);
      setTooLong(false);
    };
  }, [loading]);
  return loading && tooLong;
}

/** The price slot for a given state. Exported for the typeahead, which prices from its own
 *  dropdown-scoped lookup; every other surface uses {@link ProductPrice}. */
export function ProductPriceFigure({
  sku,
  productName,
  entry,
  asked,
  onRetry,
  loadingTooLong: callerLoadingTooLong,
  as = 'span',
  className,
}: ProductPriceFigureProps) {
  const Host = as;
  const figure = shownFigure(entry);
  // Not answered yet reads as loading; a priced entry with no figure to show (it never reaches
  // here from the client, which rejects a zero or malformed price) is a failure to price.
  const state: PriceSlotState = !asked
    ? 'not-asked'
    : figure !== null
      ? 'priced'
      : entry === undefined
        ? 'loading'
        : entry.state === 'priced'
          ? 'failed'
          : entry.state;
  const ownLoadingTooLong = useLoadingTooLong(
    callerLoadingTooLong === undefined && realPricesEnabled() && state === 'loading',
  );
  const loadingTooLong = callerLoadingTooLong ?? ownLoadingTooLong;

  if (!realPricesEnabled()) {
    return (
      <Host className={className} data-price-state={'stand-in' satisfies PriceSlotState}>
        {formatMoney(provisionalUnitPrice(sku))}
      </Host>
    );
  }
  if (figure !== null && state === 'priced') {
    return (
      <Host className={className} data-price-state={state}>
        {figure}
      </Host>
    );
  }
  if (state === 'not-asked') {
    return (
      <Host
        className={className === undefined ? styles.reserved : `${className} ${styles.reserved}`}
        data-price-state={state}
        aria-hidden="true"
      />
    );
  }
  const hostClass = className === undefined ? styles.state : `${className} ${styles.state}`;
  if (state === 'loading') {
    return (
      <Host className={hostClass} data-price-state={state}>
        {loadingTooLong ? (
          <span className={styles.line}>{LOADING_TEXT}</span>
        ) : (
          <>
            <span className={styles.placeholder} aria-hidden="true">
              {PLACEHOLDER}
            </span>
            <span className={styles.visuallyHidden}>{LOADING_SPOKEN}</span>
          </>
        )}
      </Host>
    );
  }
  if (state === 'failed') {
    return (
      <Host className={hostClass} data-price-state={state}>
        <span className={styles.line}>{FAILED_TEXT}</span>
        <button
          type="button"
          className={styles.retry}
          onClick={onRetry}
          aria-label={`Retry loading the price for ${productName}`}
        >
          Retry
        </button>
      </Host>
    );
  }
  return (
    <Host className={hostClass} data-price-state={state}>
      <span className={styles.line}>{UNPRICED_TEXT}</span>
      <span className={styles.hint}>{UNPRICED_HINT}</span>
    </Host>
  );
}

/** The price of `sku` from the shared price store. */
export function ProductPrice(props: ProductPriceProps) {
  const entry = usePriceEntry(props.sku);
  const asked = useCurrentPriceKey() !== null;
  const retry = useRetryPrice(props.sku);
  return <ProductPriceFigure {...props} entry={entry} asked={asked} onRetry={retry} />;
}

/** Whether `entry` shows as loading (asked, not answered yet, or `loading`), with the flag on. */
export function priceIsLoading(entry: PriceEntry | undefined, asked: boolean): boolean {
  return (
    realPricesEnabled() &&
    asked &&
    shownFigure(entry) === null &&
    (entry === undefined || entry.state === 'loading')
  );
}

/**
 * Whether `sku`'s Add is blocked by a price state (ADO-2248 PS-2): with the flag on and a price
 * asked for, until the product is priced (loading, failed or couldn't be priced). Only the Add:
 * a product already in the cart keeps its stepper. With the flag off, or no entry chosen (PS-9,
 * where `main`'s own no-depot block applies), it never blocks.
 */
export function addBlockedByPrice(entry: PriceEntry | undefined, asked: boolean): boolean {
  return realPricesEnabled() && asked && shownFigure(entry) === null;
}

/** {@link addBlockedByPrice} for `sku` from the shared price store. */
export function usePriceBlocksAdd(sku: string): boolean {
  const entry = usePriceEntry(sku);
  const asked = useCurrentPriceKey() !== null;
  return addBlockedByPrice(entry, asked);
}

/** The manual Retry for `sku` under the session's current entry and account (PS-4). */
export function useRetryPrice(sku: string): () => void {
  const entryKey = useSessionStore((s) => s.activeEntryKey);
  const accountCode = usePriceAccountCode();
  return () => {
    if (entryKey === null) return;
    void usePriceStore.getState().retrySku(entryKey, accountCode, sku);
  };
}

/**
 * Selector for the real RRP of `sku`, formatted, or `null` when there is none to show: a
 * priced SKU without an RRP, every non-priced state (ADO-2248 PS-3: the RSP is left out until
 * the product is priced), and a store holding entries for a key other than `currentKey` (the
 * session's, from `useCurrentPriceKey`). It returns a string, so the subscription re-renders
 * only when the text changes. With the flag off the store is empty, so the caller keeps its
 * sample RRP on that path.
 */
export function productRrp(
  sku: string,
  currentKey: string | null,
): (state: {
  readonly key: string | null;
  readonly entries: Readonly<Record<string, PriceEntry>>;
}) => string | null {
  return (state) => {
    if (currentKey === null || state.key !== currentKey) return null;
    const entry = state.entries[sku];
    return hasRrpToShow(entry) ? formatMoney(entry.rrp) : null;
  };
}

/**
 * True for a priced entry with an RSP above zero: what a card draws as the RSP
 * ({@link productRrp}) and what a list asks of a product before showing it (ADO-4197,
 * `isHiddenFromLists`), so "has an RSP" means the same thing in both.
 */
export function hasRrpToShow(
  entry: PriceEntry | undefined,
): entry is Extract<PriceEntry, { state: 'priced' }> & { readonly rrp: MoneyValue } {
  return entry?.state === 'priced' && entry.rrp !== undefined && entry.rrp.amount > 0;
}

/** The real RRP of `sku` for the session's current entry and account (ADO-2248 AC9). */
export function useProductRrp(sku: string): string | null {
  const currentKey = useCurrentPriceKey();
  return usePriceStore(productRrp(sku, currentKey));
}

/**
 * Selector for the RSP `sku` had before its promotion, formatted, or `null` when there is none to
 * show (Pricing migration 37, ADO-4034): the same states as {@link productRrp} return `null`, and
 * so does a priced SKU whose quote carries no was RSP or no RSP beside it ({@link shownWasRrp}).
 * A string, so a subscriber re-renders only when the text changes. With the flag off the store is
 * empty, so no was RSP shows (AC5).
 */
export function productWasRrp(
  sku: string,
  currentKey: string | null,
): (state: {
  readonly key: string | null;
  readonly entries: Readonly<Record<string, PriceEntry>>;
}) => string | null {
  return (state) => {
    if (currentKey === null || state.key !== currentKey) return null;
    const entry = state.entries[sku];
    if (entry?.state !== 'priced') return null;
    const wasRrp = shownWasRrp(entry);
    return wasRrp === null ? null : formatMoney(wasRrp);
  };
}

/** The was RSP of `sku` for the session's current entry and account (ADO-4034). */
export function useProductWasRrp(sku: string): string | null {
  const currentKey = useCurrentPriceKey();
  return usePriceStore(productWasRrp(sku, currentKey));
}

/**
 * What a product surface shows for the store's retail margin (Story 11.10, ADO-1961, D-3, S-9):
 * - `hidden`: no label and no value, because the account's store does not see its margin, no
 *   account is chosen, or the priced quote carries none (the read's second line of defence);
 * - `leftOut`: the price is loading, failed or unpriced, so the margin is left out as the RSP is;
 * - `shown`: the quote's margin, as its 2-decimal percent (`23.58%`, `-4.10%`, `0.00%`);
 * - `unavailable`: priced, but the margin cannot be computed; the surface shows its `—` mark.
 * Checked in that order, so a hidden margin is hidden whatever the price state.
 */
export type ProductMargin =
  | { readonly kind: 'shown'; readonly text: string }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'hidden' }
  | { readonly kind: 'leftOut' };

const HIDDEN_MARGIN: ProductMargin = { kind: 'hidden' };
const LEFT_OUT_MARGIN: ProductMargin = { kind: 'leftOut' };
const UNAVAILABLE_MARGIN: ProductMargin = { kind: 'unavailable' };

/** The margin of `sku` for the session's current entry and account (ADO-1961, D-3). */
export function useProductMargin(sku: string): ProductMargin {
  const shown = useSessionMarginShown();
  const currentKey = useCurrentPriceKey();
  // A string, so the selector's answer compares by value and the component re-renders only
  // when what it shows changes.
  const token = usePriceStore((state): string => {
    if (currentKey === null || state.key !== currentKey) return 'leftOut';
    const entry = state.entries[sku];
    if (entry?.state !== 'priced') return 'leftOut';
    if (entry.margin === undefined) return 'hidden';
    return entry.margin.status === 'available' ? `shown:${entry.margin.percent}` : 'unavailable';
  });
  return useMemo((): ProductMargin => {
    if (!shown || token === 'hidden') return HIDDEN_MARGIN;
    if (token === 'leftOut') return LEFT_OUT_MARGIN;
    if (token === 'unavailable') return UNAVAILABLE_MARGIN;
    return { kind: 'shown', text: `${token.slice('shown:'.length)}%` };
  }, [shown, token]);
}
